import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, API_URL, downloadAgentFile } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Skeleton } from '../components/Skeleton';
import CopyButton from '../components/CopyButton';
import CodeBlock from '../components/CodeBlock';
import PulseDot from '../components/PulseDot';
import Icon from '../components/Icon';
import { timeAgo } from '../lib/time';

function Section({ title, desc, children }) {
  return (
    <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
      <h3 style={{ fontSize: '1rem', marginBottom: desc ? 6 : 14 }}>{title}</h3>
      {desc && <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 14 }}>{desc}</p>}
      {children}
    </div>
  );
}

export default function Onboarding() {
  const { t, i18n } = useTranslation();
  const { activeServerKey, servers, activeServer } = useAuth();
  const lang = i18n.resolvedLanguage;
  const [loading, setLoading] = useState(true);
  const [license, setLicense] = useState(null);
  const [dlInfo, setDlInfo] = useState(null);
  const [dlState, setDlState] = useState({});
  const srv = activeServerKey || undefined;

  const load = useCallback(async () => {
    const [lic, info] = await Promise.all([api.license(srv), api.downloadInfo().catch(() => null)]);
    setLicense(lic.license);
    setDlInfo(info);
  }, [srv]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    const id = setInterval(() => api.license(srv).then((r) => setLicense(r.license)).catch(() => {}), 15000);
    return () => clearInterval(id);
  }, [load, srv]);

  const doDownload = async (platform) => {
    setDlState((s) => ({ ...s, [platform]: 'loading' }));
    try {
      await downloadAgentFile(platform);
      setDlState((s) => ({ ...s, [platform]: null }));
    } catch (e) {
      setDlState((s) => ({ ...s, [platform]: e.message || 'error' }));
      setTimeout(() => setDlState((s) => ({ ...s, [platform]: null })), 5000);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t('onboarding.title')} heading={false}>
        <Skeleton style={{ height: 24, width: 160 }} />
        <Skeleton className="sk-block" style={{ marginTop: 28 }} />
      </DashboardLayout>
    );
  }

  if (!license) {
    return (
      <DashboardLayout title={t('onboarding.title')} heading={false}>
        <h1 className="page-title">{t('onboarding.title')}</h1>
        <div className="empty-state" style={{ marginTop: 'var(--sp-5)' }}>
          <Icon name="key" size={28} style={{ color: 'var(--text-3)' }} />
          <h3 style={{ marginTop: 12 }}>{t('onboarding.no_license')}</h3>
          <Link to="/checkout" className="btn btn-sm" style={{ marginTop: 'var(--sp-4)' }}>{t('checkout.title')}</Link>
        </div>
      </DashboardLayout>
    );
  }

  const key = license.key;
  const origin = (API_URL && /^https?:\/\//.test(API_URL) ? API_URL : window.location.origin).replace(/\/+$/, '');
  const linuxCmd = `curl -fsSL ${origin}/kur/${key} | sudo bash`;
  const winCmd = `irm ${origin}/kur/${key}.ps1 | iex`;
  const bsdCmd = `fetch -qo - ${origin}/kur/${key}.freebsd | sh`;

  const hb = license.lastHeartbeatAt;
  const statusState = !hb ? 'waiting' : license.agentStatus === 'active' ? 'active' : 'silent';
  const statusTone = statusState === 'active' ? 'ok' : statusState === 'silent' ? 'crit' : 'idle';
  const schemaPending = license.schemaStatus === 'pending';
  const schemaConfirmed = license.schemaStatus === 'confirmed';

  const winType = dlInfo?.windows?.type;
  const linType = dlInfo?.linux?.type;
  const anyBundle = winType === 'bundle' || linType === 'bundle' || (!winType && !linType);
  const runFile = anyBundle ? 'vokelle-agent.cjs' : 'vokelle-agent-linux';

  const sqlBlock = `CREATE USER 'vokelle_ro'@'%' IDENTIFIED BY 'GUCLU_BIR_SIFRE';
GRANT SELECT ON *.* TO 'vokelle_ro'@'%';
FLUSH PRIVILEGES;`;

  const configBlock = JSON.stringify(
    {
      licenseKey: key,
      db: { host: '127.0.0.1', port: 3306, user: 'vokelle_ro', password: 'GUCLU_BIR_SIFRE' },
      reportIntervalMinutes: 10,
      backendUrl: origin,
    },
    null,
    2
  );

  const systemdBlock = `[Unit]
Description=Vokelle Agent
After=network.target mysql.service

[Service]
Type=simple
WorkingDirectory=/opt/vokelle
ExecStart=/usr/bin/node /opt/vokelle/${runFile}
Restart=always
RestartSec=15

[Install]
WantedBy=multi-user.target`;

  const DlButton = ({ platform, labelKey }) => {
    const info = dlInfo?.[platform];
    const st = dlState[platform];
    const disabled = info && !info.available;
    return (
      <button className="btn btn-subtle" onClick={() => doDownload(platform)} disabled={disabled || st === 'loading'}
        style={disabled ? { opacity: 0.5 } : undefined}>
        <Icon name="download" size={14} />
        {st === 'loading' ? t('onboarding.downloading') : t(labelKey)}
      </button>
    );
  };

  return (
    <DashboardLayout title={t('onboarding.title')} heading={false}>
      <h1 className="page-title">{t('onboarding.title')}</h1>
      <p className="page-sub">{t('onboarding.subtitle')}</p>

      {servers && servers.length > 1 && (
        <div className="badge" style={{ marginBottom: 'var(--sp-4)' }}>
          <Icon name="activity" size={12} />
          {t('onboarding.for_server', { name: activeServer?.label || t('servers.default_name', { n: (activeServer?.order ?? 0) + 1 }) })}
        </div>
      )}

      {schemaPending && (
        <div className="card" style={{ marginBottom: 'var(--sp-4)', borderColor: 'var(--border-accent)', background: 'var(--warn-soft)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <Icon name="alert-triangle" size={18} style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong style={{ display: 'block', marginBottom: 4 }}>{t('onboarding.schema_pending_title')}</strong>
              <p style={{ color: 'var(--text-2)', fontSize: 13, margin: '0 0 12px' }}>{t('onboarding.schema_pending_desc')}</p>
              <Link to="/onboarding/schema-confirm" className="btn btn-sm">{t('onboarding.schema_pending_cta')}</Link>
            </div>
          </div>
        </div>
      )}
      {schemaConfirmed && !hb && (
        <div className="badge ok" style={{ marginBottom: 'var(--sp-4)' }}>
          <Icon name="check" size={12} /> {t('onboarding.schema_confirmed')}
        </div>
      )}
      {!schemaPending && hb && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
          marginBottom: 'var(--sp-4)', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)' }}>
          <span style={{ color: 'var(--text-2)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Icon name={schemaConfirmed ? 'check' : 'hash'} size={14} style={{ color: schemaConfirmed ? 'var(--ok)' : 'var(--text-3)' }} />
            {schemaConfirmed ? t('onboarding.schema_override_active') : t('onboarding.schema_auto_active')}
          </span>
          <Link to="/onboarding/schema-confirm" className="btn btn-subtle btn-sm">{t('onboarding.schema_edit_cta')}</Link>
        </div>
      )}

      {/* Ajan durumu */}
      <div className="card" style={{
        marginBottom: 'var(--sp-5)', borderColor: statusState === 'active' ? 'var(--border-accent)' : 'var(--border)',
        display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', justifyContent: 'space-between', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <PulseDot tone={statusTone} animated={statusState === 'active'} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{t('onboarding.status_title')}</div>
            <div style={{ color: 'var(--text-3)', fontSize: 13 }}>
              {statusState === 'waiting' ? t('onboarding.status_waiting') : t(`onboarding.status_${statusState}`, { ago: timeAgo(hb, lang) })}
            </div>
          </div>
        </div>
        <button className="btn btn-subtle btn-sm" onClick={() => api.license(srv).then((r) => setLicense(r.license))}>
          <Icon name="activity" size={13} /> {t('onboarding.refresh')}
        </button>
      </div>

      {/* TEK KOMUT */}
      <div className="card" style={{ marginBottom: 'var(--sp-4)', borderColor: 'var(--border-accent)' }}>
        <h3 style={{ fontSize: '1.05rem', marginBottom: 6 }}>{t('onboarding.oneclick_title')}</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 16 }}>{t('onboarding.oneclick_desc')}</p>

        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('onboarding.oneclick_linux')}</div>
        <CodeBlock code={linuxCmd} />

        <div style={{ fontWeight: 600, fontSize: 13, margin: '16px 0 6px' }}>{t('onboarding.oneclick_windows')}</div>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: '0 0 6px' }}>{t('onboarding.oneclick_windows_hint')}</p>
        <CodeBlock code={winCmd} />

        <div style={{ fontWeight: 600, fontSize: 13, margin: '16px 0 6px' }}>{t('onboarding.oneclick_freebsd')}</div>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: '0 0 6px' }}>{t('onboarding.oneclick_freebsd_hint')}</p>
        <CodeBlock code={bsdCmd} />

        <ul style={{ margin: '16px 0 0', paddingLeft: 18, color: 'var(--text-2)', fontSize: 12.5, display: 'grid', gap: 6 }}>
          <li>{t('onboarding.oneclick_note1')}</li>
          <li>{t('onboarding.oneclick_note2')}</li>
          <li>{t('onboarding.oneclick_note3')}</li>
          <li>{t('onboarding.oneclick_note4')}</li>
        </ul>
      </div>

      {/* Discord */}
      <Section title={t('onboarding.discord_title')} desc={t('onboarding.discord_desc')}>
        <Link to="/dashboard/notifications" className="btn btn-subtle btn-sm">
          <Icon name="bell" size={13} /> {t('onboarding.go_notifications')}
        </Link>
      </Section>

      {/* MANUEL KURULUM (ileri düzey) */}
      <details className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>{t('onboarding.manual_title')}</summary>
        <p style={{ color: 'var(--text-3)', fontSize: 13, margin: '10px 0 18px' }}>{t('onboarding.manual_desc')}</p>

        <Section title={t('onboarding.download_title')} desc={t('onboarding.download_desc')}>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <DlButton platform="windows" labelKey="onboarding.download_windows" />
            <DlButton platform="linux" labelKey="onboarding.download_linux" />
          </div>
          <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 10 }}>
            {!dlInfo || (!dlInfo.windows.available && !dlInfo.linux.available)
              ? t('onboarding.download_soon')
              : anyBundle ? t('onboarding.download_bundle_note') : t('onboarding.download_binary_note')}
          </p>
        </Section>

        <Section title={t('onboarding.key_title')} desc={t('onboarding.key_desc')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: '10px 14px', flexWrap: 'wrap' }}>
            <code style={{ fontSize: 15, letterSpacing: '0.04em', color: 'var(--accent)', fontFamily: 'ui-monospace, monospace' }}>{key}</code>
            <CopyButton text={key} />
          </div>
        </Section>

        <Section title={t('onboarding.db_title')} desc={t('onboarding.db_desc')}>
          <CodeBlock code={sqlBlock} />
          <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 10 }}>{t('onboarding.db_note')}</p>
        </Section>

        <Section title={t('onboarding.config_title')} desc={t('onboarding.config_desc')}>
          <CodeBlock code={configBlock} />
        </Section>

        <Section title={t('onboarding.run_title')}>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-2)', fontSize: 13.5, display: 'grid', gap: 8 }}>
            <li>{anyBundle ? t('onboarding.run_bundle_windows') : t('onboarding.run_bin_windows')}</li>
            <li>{anyBundle ? t('onboarding.run_bundle_linux') : t('onboarding.run_bin_linux')}</li>
          </ul>
          <div style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{t('onboarding.systemd_title')}</div>
            <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: '0 0 10px' }}>{t('onboarding.systemd_desc')}</p>
            <CodeBlock code={systemdBlock} />
          </div>
        </Section>
      </details>
    </DashboardLayout>
  );
}
